import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import {
  injectMutation,
  injectQuery,
  QueryClient,
} from '@tanstack/angular-query-experimental';
import { firstValueFrom, lastValueFrom, take } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../auth/services/auth.service';
import { ai as AiGen } from '@valvur/shared-grpc';
import { challenge as ChallengeGen } from '@valvur/shared-grpc';
import { CourseSettingsData } from '../models';
import { StudentCompactItem } from '../models/student.models';

const DRAFT_STATUS = ['DRAFT', 'READY_FOR_VIDEO_GENERATION'];

const TOP_PERFORMANCE_PAGE_SIZE = 10;

/** API response for GET /courses/my-students */
export interface EnrolledStudentsRes {
  data: Array<{
    userId: string;
    userName: string;
    coursesJoined: number;
    courseProgress: Array<{
      courseId: string;
      courseName: string;
      completionPercent: number;
      completedTasks: number;
      totalTasks: number;
    }>;
  }>;
  total?: number;
}

@Injectable({ providedIn: 'root' })
export class CourseService {
  #http = inject(HttpClient);
  #authService = inject(AuthService);
  #queryClient = inject(QueryClient);

  /**
   * Enables GET /courses and GET /courses/my-students (school “My courses” layout only).
   * Do not set from admin layouts — use AdminContentService for catalog there.
   */
  schoolTeacherCoursesListsEnabled = signal(false);
  isMyStudiesPageEnabled = signal(false);
  isCourseDetailPageEnabled = signal(false);
  /**
   * Admin VALVUR content layout: video/audio/publish mutations must refresh only
   * `['valvurCourses']`, not school `GET /courses` or learner `studyingCourses`.
   * Set while the admin content (VALVUR catalog) page is active; cleared on destroy.
   */
  adminValvurMutationIsolation = signal(false);
  hasDrafts = signal(false);
  hasNotPublishedCourses = signal(false);
  // Signal to track if any videos are generating (updated in query select to avoid cycles)
  hasGeneratingVideos = signal(false);

  // tasks = computed(() => this.#tasks.data()?.tasks);
  courses = computed(() => this.#courses.data());
  studyingCourses = computed(() => this.#studyingCourses.data());
  drafts = computed(() =>
    this.courses()?.filter((course) => DRAFT_STATUS.includes(course.status)),
  );
  publishedCourses = computed(
    () =>
      this.courses()?.filter((course) => course.status === 'PUBLISHED') ?? [],
  );
  // completionRate = computed(() => this.#tasks.data()?.completionRate);
  studyingCompletionRate = computed(
    () => this.#studyingCourses.data()?.completionRate,
  );

  isLoading = computed(() => this.#studyingCourses.isLoading());

  #courses = injectQuery(() => ({
    queryKey: ['courses'],
    retry: 1,
    queryFn: () =>
      lastValueFrom(
        this.#http
          .get<ChallengeGen.Course[]>(`${environment.baseUrl}/courses`)
          .pipe(take(1)),
      ),
    select: (data: ChallengeGen.Course[]) => {
      this.hasDrafts.set(
        Array.isArray(data) && data.some((course) => course.status === 'DRAFT'),
      );
      this.hasNotPublishedCourses.set(
        Array.isArray(data) &&
          data.some((course) => DRAFT_STATUS.includes(course.status)),
      );
      return data;
    },
    enabled:
      this.#authService.isAuthenticated() &&
      this.schoolTeacherCoursesListsEnabled(),
    refetchInterval: (): number | false => {
      const shouldPoll =
        this.schoolTeacherCoursesListsEnabled() &&
        (this.hasDrafts() || this.hasGeneratingVideos());
      return shouldPoll ? 60000 : false;
    },
  }));

  #studyingCourses = injectQuery(() => ({
    queryKey: [
      'studyingCourses',
      this.isMyStudiesPageEnabled(),
      this.isCourseDetailPageEnabled(),
    ],
    retry: 0,
    queryFn: () =>
      lastValueFrom(
        this.#http
          .get<ChallengeGen.CourseRes>(
            `${environment.baseUrl}/courses/available`,
          )
          .pipe(take(1)),
      ),
    select: (data: ChallengeGen.CourseRes) => {
      return {
        courses: data.data,
        completionRate: data.completionRate ?? 0,
      };
    },
    enabled:
      this.#authService.isAuthenticated() &&
      (this.isMyStudiesPageEnabled() || this.isCourseDetailPageEnabled()),
  }));

  joinCourse = injectMutation(() => ({
    mutationFn: (courseId: string) =>
      lastValueFrom(
        this.#http
          .post<ChallengeGen.Course>(
            `${environment.baseUrl}/courses/${courseId}/join`,
            {},
          )
          .pipe(take(1)),
      ),
    onSuccess: () => {
      this.#studyingCourses.refetch();
    },
  }));

  leaveCourse = injectMutation(() => ({
    mutationFn: (courseId: string) =>
      lastValueFrom(
        this.#http
          .post<ChallengeGen.Course>(
            `${environment.baseUrl}/courses/${courseId}/leave`,
            {},
          )
          .pipe(take(1)),
      ),
    onSuccess: () => {
      this.#studyingCourses.refetch();
    },
  }));

  rateCourse = injectMutation(() => ({
    mutationFn: (dto: Partial<ChallengeGen.RateCourseDto>) => {
      const res = lastValueFrom(
        this.#http
          .post<ChallengeGen.Rating>(`${environment.baseUrl}/courses/rate`, dto)
          .pipe(take(1)),
      );
      return res;
    },
    onSuccess: () => {
      this.#studyingCourses.refetch();
    },
  }));

  generateVideo = injectMutation(() => ({
    mutationFn: (dto: AiGen.GenerateVideoRequestDto) => {
      const res = lastValueFrom(
        this.#http
          .post<ChallengeGen.Task>(
            `${environment.baseUrl}/tasks/generate-video`,
            dto,
          )
          .pipe(take(1)),
      );
      return res;
    },
    onSuccess: () => {
      const adminOnly = this.adminValvurMutationIsolation();
      if (!adminOnly) {
        this.#courses.refetch();
      }
      void this.#queryClient.invalidateQueries({ queryKey: ['valvurCourses'] });
      if (
        !adminOnly &&
        (this.isMyStudiesPageEnabled() || this.isCourseDetailPageEnabled())
      ) {
        void this.#queryClient.invalidateQueries({
          queryKey: ['studyingCourses'],
        });
      }
    },
  }));

  generateAudio = injectMutation(() => ({
    mutationFn: (dto: AiGen.GenerateAudioRequestDto) => {
      const res = lastValueFrom(
        this.#http
          .post<ChallengeGen.Task>(
            `${environment.baseUrl}/tasks/generate-audio`,
            dto,
          )
          .pipe(take(1)),
      );
      return res;
    },
    onSuccess: () => {
      const adminOnly = this.adminValvurMutationIsolation();
      if (!adminOnly) {
        this.#courses.refetch();
      }
      void this.#queryClient.invalidateQueries({ queryKey: ['valvurCourses'] });
      if (
        !adminOnly &&
        (this.isMyStudiesPageEnabled() || this.isCourseDetailPageEnabled())
      ) {
        void this.#queryClient.invalidateQueries({
          queryKey: ['studyingCourses'],
        });
      }
    },
  }));

  createCourse = injectMutation(() => ({
    mutationFn: (dto: CourseSettingsData) => {
      const formData = this.#createFormData(dto);
      const res = lastValueFrom(
        this.#http
          .post<{ data: any }>(`${environment.baseUrl}/courses`, formData)
          .pipe(take(1)),
      );
      return res;
    },
    onSuccess: () => {
      this.#courses.refetch();
    },
  }));

  /** Admin: JSON body, topic + VALVUR catalog (POST /courses/valvur). */
  createValvurCourse = injectMutation(() => ({
    mutationFn: (dto: CourseSettingsData) =>
      lastValueFrom(
        this.#http
          .post<ChallengeGen.Course>(
            `${environment.baseUrl}/courses/valvur`,
            dto,
          )
          .pipe(take(1)),
      ),
    onSuccess: () => {
      void this.#queryClient.invalidateQueries({ queryKey: ['valvurCourses'] });
    },
  }));

  publishCourse = injectMutation(() => ({
    mutationFn: (courseId: string) =>
      lastValueFrom(
        this.#http
          .post<ChallengeGen.Course>(
            `${environment.baseUrl}/courses/${courseId}/publish`,
            {},
          )
          .pipe(take(1)),
      ),
    onSuccess: () => {
      void this.#queryClient.invalidateQueries({ queryKey: ['valvurCourses'] });
      const adminOnly = this.adminValvurMutationIsolation();
      if (
        !adminOnly &&
        (this.isMyStudiesPageEnabled() || this.isCourseDetailPageEnabled())
      ) {
        void this.#queryClient.invalidateQueries({
          queryKey: ['studyingCourses'],
        });
      }
      if (!adminOnly) {
        this.#courses.refetch();
      }
    },
  }));

  updateValvurCourse = injectMutation(() => ({
    mutationFn: (dto: { courseId: string } & Record<string, unknown>) => {
      const { courseId, ...body } = dto;
      return lastValueFrom(
        this.#http
          .patch<ChallengeGen.Course>(
            `${environment.baseUrl}/courses/valvur/${courseId}`,
            body,
          )
          .pipe(take(1)),
      );
    },
    onSuccess: () => {
      void this.#queryClient.invalidateQueries({ queryKey: ['valvurCourses'] });
    },
  }));

  uploadValvurCourseAsset = injectMutation(() => ({
    mutationFn: (dto: {
      courseId: string;
      assetType: string;
      taskId?: string;
      file: File;
    }) => {
      const formData = new FormData();
      formData.append('file', dto.file, dto.file.name);
      formData.append('assetType', dto.assetType);
      if (dto.taskId) {
        formData.append('taskId', dto.taskId);
      }
      return lastValueFrom(
        this.#http
          .post<ChallengeGen.Course>(
            `${environment.baseUrl}/courses/valvur/${dto.courseId}/assets`,
            formData,
          )
          .pipe(take(1)),
      );
    },
    onSuccess: () => {
      void this.#queryClient.invalidateQueries({ queryKey: ['valvurCourses'] });
    },
  }));

  /**
   * Gets a course by its ID from the cached courses
   * @param courseId - The ID of the course to retrieve
   * @returns The course if found, undefined otherwise
   */
  getCourseById(courseId: string): ChallengeGen.Course | undefined {
    const allCourses = this.courses();
    return allCourses?.find((course) => course.id === courseId);
  }

  /**
   * Gets a course by its ID from studying/available courses
   */
  getStudyingCourseById(courseId: string): ChallengeGen.Course | undefined {
    const courses = this.studyingCourses();
    return courses?.courses?.find((course) => course.id === courseId);
  }

  refetchStudyingCourses(): Promise<unknown> {
    return this.#studyingCourses.refetch();
  }

  #enrolledStudents = injectQuery(() => ({
    queryKey: ['enrolledStudents', this.schoolTeacherCoursesListsEnabled()],
    retry: 1,
    queryFn: () =>
      lastValueFrom(
        this.#http
          .get<EnrolledStudentsRes>(
            `${environment.baseUrl}/courses/my-students`,
          )
          .pipe(take(1)),
      ),
    enabled:
      this.#authService.isAuthenticated() &&
      this.schoolTeacherCoursesListsEnabled(),
  }));

  enrolledStudents = computed(() => this.#enrolledStudents.data()?.data ?? []);

  enrolledStudentsLoading = computed(() => this.#enrolledStudents.isLoading());

  refetchEnrolledStudents(): Promise<unknown> {
    return this.#enrolledStudents.refetch();
  }

  /** Top Performance: paginated students sorted by avg completion */
  topPerformancePage = signal(1);

  #topPerformanceStudents = injectQuery(() => ({
    queryKey: [
      'topPerformanceStudents',
      this.topPerformancePage(),
      this.schoolTeacherCoursesListsEnabled(),
    ],
    retry: 1,
    queryFn: () =>
      lastValueFrom(
        this.#http
          .get<EnrolledStudentsRes>(
            `${environment.baseUrl}/courses/my-students`,
            {
              params: {
                page: this.topPerformancePage(),
                limit: TOP_PERFORMANCE_PAGE_SIZE,
              },
            },
          )
          .pipe(take(1)),
      ),
    enabled:
      this.#authService.isAuthenticated() &&
      this.schoolTeacherCoursesListsEnabled(),
  }));

  topPerformanceStudents = computed<StudentCompactItem[]>(() => {
    const raw = this.#topPerformanceStudents.data()?.data ?? [];
    return raw.map((s) => {
      const avgPercent =
        s.courseProgress?.length > 0
          ? Math.round(
              s.courseProgress.reduce(
                (sum, p) => sum + p.completionPercent,
                0,
              ) / s.courseProgress.length,
            )
          : 0;
      const coursesLabel =
        s.coursesJoined === 1 ? '1 course' : `${s.coursesJoined} courses`;
      return {
        name: s.userName || s.userId,
        coursesLabel,
        percentage: avgPercent,
        ratingBadge: `${avgPercent}%`,
        avatarUrl: '',
      } satisfies StudentCompactItem;
    });
  });

  topPerformanceTotal = computed(
    () => this.#topPerformanceStudents.data()?.total ?? 0,
  );

  topPerformanceTotalPages = computed(() =>
    Math.max(
      1,
      Math.ceil(this.topPerformanceTotal() / TOP_PERFORMANCE_PAGE_SIZE),
    ),
  );

  topPerformanceLoading = computed(() =>
    this.#topPerformanceStudents.isLoading(),
  );

  setTopPerformancePage(page: number): void {
    this.topPerformancePage.set(Math.max(1, page));
  }

  refetchTopPerformance(): Promise<unknown> {
    return this.#topPerformanceStudents.refetch();
  }

  /** Lookup user by 6-char invite code */
  lookupByInviteCode = injectMutation(() => ({
    mutationFn: (code: string) =>
      lastValueFrom(
        this.#http
          .get<{
            id: string;
            userName: string;
            personalData?: { firstName?: string; lastName?: string };
          }>(`${environment.baseUrl}/user/lookup-by-code`, { params: { code } })
          .pipe(take(1)),
      ),
  }));

  /** Add user to course by 6-char invite code */
  addUserByInviteCode = injectMutation(() => ({
    mutationFn: (dto: { inviteCode: string; courseId: string }) =>
      lastValueFrom(
        this.#http
          .post<ChallengeGen.Course>(
            `${environment.baseUrl}/courses/add-user-by-code`,
            dto,
          )
          .pipe(take(1)),
      ),
    onSuccess: () => {
      this.#courses.refetch();
      this.#enrolledStudents.refetch();
      this.#topPerformanceStudents.refetch();
    },
  }));

  /** Remove user from course */
  removeUserFromCourse = injectMutation(() => ({
    mutationFn: (dto: { userId: string; courseId: string }) =>
      lastValueFrom(
        this.#http
          .post<ChallengeGen.Course>(
            `${environment.baseUrl}/courses/remove-user`,
            dto,
          )
          .pipe(take(1)),
      ),
    onSuccess: () => {
      this.#courses.refetch();
      this.#enrolledStudents.refetch();
      this.#topPerformanceStudents.refetch();
    },
  }));

  /**
   * Fetches and formats a script from a URL
   * @param scriptUrl - The URL to fetch the script from
   * @returns The formatted script text (stringified JSON if object, or string as-is)
   */
  async fetchScript(scriptUrl: string): Promise<string> {
    const scriptData = await firstValueFrom(
      this.#http.get<{ script: string }>(scriptUrl),
    );

    return scriptData.script;
  }

  /**
   * Converts CourseSettingsData into FormData for backend submission
   * @param data - The course settings data to convert
   * @returns FormData object ready for HTTP request
   */
  #createFormData(data: CourseSettingsData): FormData {
    const formData = new FormData();

    // Append files
    if (data.files && data.files.length > 0) {
      data.files.forEach((file, index) => {
        formData.append('files', file, file.name);
        // Optionally append file metadata
        formData.append(`file_${index}_name`, file.name);
        formData.append(`file_${index}_size`, file.size.toString());
        formData.append(`file_${index}_type`, file.type);
      });
    }

    // Append main info fields
    if (data.discipline) {
      formData.append('discipline', data.discipline);
    }
    if (data.audience) {
      formData.append('audience', data.audience);
    }
    if (data.tone) {
      formData.append('tone', data.tone);
    }

    // Append structure fields
    if (data.quantity) {
      formData.append('quantity', data.quantity);
    }
    if (data.duration) {
      formData.append('duration', data.duration);
    }
    if (data.courseSource) {
      formData.append('courseSource', data.courseSource);
    }

    return formData;
  }
}
